import { useEffect } from "react";
import { Link } from "react-router-dom";
import { seoArticlesList } from "@/mocks/seoArticlesList";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function ShoppingGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "CollectionPage",
              "name": "Shopping Guide – ItaobuySpreadsheet",
              "description": "Expert guides, comparisons, and tutorials to help you master China product sourcing with the itaobuyspreadsheet.",
              "url": "https://itaobuy.cfd/shopping-guide",
              "mainEntity": {
                "@type": "ItemList",
                "itemListElement": [
                  { "@type": "ListItem", "position": 1, "item": { "@type": "Article", "name": "Complete ItaobuySpreadsheet Guide", "url": "https://itaobuy.cfd/itaobuyspreadsheet-guide" } },
                  ...seoArticlesList.map((article, idx) => ({
                    "@type": "ListItem",
                    "position": idx + 2,
                    "item": { "@type": "Article", "name": article.title, "url": "https://itaobuy.cfd/article/" + article.slug }
                  }))
                ]
              }
            })
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://itaobuy.cfd/" },
                { "@type": "ListItem", "position": 2, "name": "Shopping Guide", "item": "https://itaobuy.cfd/shopping-guide" }
              ]
            })
          }}
        />
        <section className="w-full bg-background-100 pt-32 pb-20 px-6 md:px-10">
          <div className="max-w-7xl mx-auto text-center">
            <p className="font-heading text-xs md:text-sm text-foreground-500 uppercase tracking-[0.2em] font-semibold">
              Knowledge Hub
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mt-3">
              Shopping Guide
            </h1>
            <p className="text-foreground-600 text-sm md:text-base mt-4 max-w-xl mx-auto">
              Expert guides, comparisons, and tutorials to help you master China product sourcing with the itaobuyspreadsheet.
            </p>
          </div>
        </section>

        <section className="w-full bg-background-50 py-16 md:py-24 px-6 md:px-10">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="rounded-2xl overflow-hidden bg-foreground-950 text-white group cursor-pointer transition-transform hover:-translate-y-1">
                <div className="h-48 overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Elegant%20minimalist%20workspace%20with%20an%20open%20laptop%20showing%20spreadsheet%20interface%20on%20screen%20surrounded%20by%20product%20samples%20and%20sourcing%20notes%2C%20warm%20cream%20and%20beige%20desk%20setup%2C%20soft%20natural%20window%20lighting%2C%20neutral%20aesthetic%2C%20editorial%20productivity%20photography%20with%20shallow%20depth%20of%20field%20and%20gentle%20shadows&width=800&height=500&seq=guide-hero-main&orientation=landscape"
                    alt="ItaobuySpreadsheet Complete Guide"
                    className="w-full h-full object-cover object-top"
                  />
                </div>
                <div className="p-6">
                  <span className="inline-block px-2 py-0.5 bg-accent-500 text-white text-xs font-semibold rounded mb-3">
                    Featured Guide
                  </span>
                  <h3 className="font-heading text-xl font-bold mb-2">
                    Complete ItaobuySpreadsheet Guide
                  </h3>
                  <p className="text-white/60 text-sm mb-4">
                    The ultimate resource covering everything from product discovery to scaling your eCommerce store.
                  </p>
                  <Link
                    to="/itaobuyspreadsheet-guide"
                    className="inline-flex items-center gap-2 text-sm font-semibold text-accent-400 hover:text-accent-300 transition-colors whitespace-nowrap"
                  >
                    Read Full Guide
                    <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                  </Link>
                </div>
              </div>

              {seoArticlesList.map((article) => (
                <Link
                  key={article.slug}
                  to={`/article/${article.slug}`}
                  className="rounded-2xl overflow-hidden bg-background-100 border border-background-200/70 group cursor-pointer transition-transform hover:-translate-y-1"
                >
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <span className="text-xs text-foreground-500">{article.datePublished}</span>
                    <h3 className="font-heading text-lg font-bold text-foreground-950 mt-2 mb-2 leading-snug">
                      {article.title}
                    </h3>
                    <p className="text-foreground-600 text-sm line-clamp-2">
                      {article.summary}
                    </p>
                    <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-primary-600 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                      Read Article
                      <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="w-full bg-background-100 py-16 md:py-24 px-6 md:px-10 text-center">
          <div className="max-w-2xl mx-auto">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-4">
              Ready to Start Sourcing?
            </h2>
            <p className="text-foreground-600 text-sm mb-8">
              Reading is great, but doing is better. Visit the main platform to start exploring products today.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-primary-500 text-white rounded-lg text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                itaobuy sheet
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="inline-flex items-center justify-center gap-2 px-8 py-3.5 bg-foreground-950 text-white rounded-lg text-sm font-semibold hover:bg-foreground-800 transition-colors whitespace-nowrap"
              >
                buy it now
                <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
              </a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}